import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import HelpIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Box, Button, DialogContent, Divider, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { currentContextAtom } from '../../../state';
import CopyToClipboard from '../../../common/components/CopyToClipboard';
import { BuyerInfoViewProps } from './BuyerInfoDialog';

export default function BuyerInfoInvoicingHowToView({ company, close, changeView }: BuyerInfoViewProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'contracts', 'order']);
  const { companyInvoicePrefix } = useAtomValue(currentContextAtom);

  const invoicingEmailAddress = useMemo(() => {
    const buyerPrefix = company.invoice_prefix;
    const supplierPrefix = companyInvoicePrefix;

    return `invoices-${buyerPrefix}-${supplierPrefix}@${import.meta.env.REACT_APP_KHEOPS_INVOICING_DOMAIN}`;
  }, [company]);

  return (
    <DialogContent
      sx={{
        maxWidth: 500,
        height: 620,
        p: 2,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="text"
            onClick={() => changeView('INVOICING')}
          >
            <ArrowBackIcon />
          </Button>
          <HelpIcon sx={{ mx: 1 }} />
          <Typography variant="titleMedium" sx={{ flex: 1 }}>
            {t('contracts:invoicing_how_to_send_invoices')}
          </Typography>
          <Button variant="text" onClick={close}>
            <CloseIcon />
          </Button>
        </Box>
        <Typography variant="bodySmall" color="secondary">
          {t('contracts:invoicing_how_to_send_invoices_description')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          px: 1,
          gap: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography variant="titleMedium">
            {t('order:from_kheops')}
          </Typography>
          <Typography
            variant="bodyMedium"
            sx={{ pl: 1 }}
          >
            {t('contracts:invoicing_how_to_join_invoices_on_kheops')}
          </Typography>
        </Box>
        <Divider />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography variant="titleMedium">
            {t('order:by_email')}
          </Typography>
          <Typography
            variant="bodyMedium"
            sx={{ pl: 1 }}
          >
            {t('contracts:invoicing_how_to_send_invoices_by_email')}
          </Typography>
          <Typography
            variant="bodyLarge"
            sx={{ pl: 1 }}
          >
            {t('order:dedicated_email')}
          </Typography>
          <CopyToClipboard textToCopy={invoicingEmailAddress}>
          <Typography variant="bodyMedium" sx={{ pl: 1 }}>
              <a href={`mailto:${invoicingEmailAddress}`} style={{ color: 'inherit' }}>
                {invoicingEmailAddress}
              </a>
            </Typography>
          </CopyToClipboard>
        </Box>
      </Box>
    </DialogContent>
  );
}
